import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { supabase } from "../config/supabaseClient";
import { startLoading, stopLoading } from "./commonSlice";
import _ from "lodash";
import { toast } from "react-toastify";
import { messages } from "../constant/alertMessages";
import axios from "axios";
import { COMPANY_PRIVILEGED_ROLES, roleType, statuses, jobType } from "../constant/constants";
import { getLocalData, normalizeDepartmentIdForSave, sendInvite } from "../constant/utils/commonUtils";

const isIgnorableAuthDeleteError = (error) =>
  error?.code === "user_not_found" || error?.message === "User not found";

/** Delete auth user; treat missing user as already removed (common after import-only employees). */
const deleteAuthUserSafely = async (userId) => {
  const { error } = await supabase.auth.admin.deleteUser(userId);
  if (error && !isIgnorableAuthDeleteError(error)) {
    throw error;
  }
};

export const fetchEmployees = createAsyncThunk(
  "employees/fetchEmployees",
  async (companyId, { rejectWithValue, dispatch }) => {
    try {
      dispatch(startLoading());
      const { data, error } = await supabase.rpc(
        "get_company_employees_details",
        {
          p_company_id: companyId,
        }
      );

      if (error) throw error;

      return data;
      // return data;
    } catch (error) {
      return rejectWithValue(error.message);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const fetchEmployeeById = createAsyncThunk(
  "employees/fetchEmployeeById",
  async ({ employeeId, companyId }, { rejectWithValue }) => {
    try {
     
      const { data, error } = await supabase
        .from("company_user")
        .select("id,company_id,data,user_id,emp_id,stipend_amount,employment_type,department_id,first_name,last_name,email")
        .eq("id", employeeId)
        .eq("company_id", companyId)
        .single(); // Fetch a single employee record

      if (error) throw error;

      // Check for duplicate emp_id if the employee has one
      let hasDuplicateEmpId = false;
      if (data.emp_id) {
        const { data: existingEmployees, error: duplicateError } = await supabase
          .from("company_user")
          .select("id,emp_id")
          .eq("company_id", companyId)
          .eq("emp_id", data.emp_id)
          .neq("status", "ARCHIVED") 
          .neq("id", employeeId);

        if (!duplicateError && existingEmployees && existingEmployees.length > 0) {
          hasDuplicateEmpId = true;
        }
      }

      return {
        ...data,
        hasDuplicateEmpId, 
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const uploadEmployees = createAsyncThunk(
  "employees/uploadEmployees",
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/uploadEmployeesData`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${process.env.REACT_APP_BACKEND_API_KEY}`,
          },
        }
      );

      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Update Employee
export const updateEmployee = createAsyncThunk(
  "employees/updateEmployee",
  async (data, { rejectWithValue, dispatch }) => {
    try {
      dispatch(startLoading());
      // if (!data.invited && data.isWorkflowReq) {
      //   const res = await sendInvite(null, data.new.newData.email, roleType.EMPLOYEE);

      //   if (!res.success) {
      //     return rejectWithValue("invalid email");
      //   }
      //   data.new.newData.user_id = res.userId;
      // }

      const payload = { ...data.new.newData };
      payload.department_id = normalizeDepartmentIdForSave(payload.department_id);

      const { data: updatedData, error } = await supabase
        .from("company_user")
        .update(payload)
        .eq("id", data.new.id)
        .select("*");
      if (error) throw error;
      return updatedData;
    } catch (error) {
      return rejectWithValue(error.message);
    } finally {
      dispatch(stopLoading());
    }
  }
);

// Delete Employee
export const deleteEmployee = createAsyncThunk(
  "employees/deleteEmployee",
  async (id, { rejectWithValue }) => {
    try {
      const { data: deletedData, error } = await supabase
        .from("company_user")
        .update({ status: statuses.ARCHIVED})
        .eq("id", id)
        .select("user_id")
        .single();
      // console.log("deletedData", deletedData);
      if (error) throw error;

      // Delete auth account when present (skip if already removed from auth)
      if (deletedData.user_id) {
        await deleteAuthUserSafely(deletedData.user_id);
      }

      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/** Clear imported SETA fields for one SDF/company_admin (same as Remove All per privileged user). */
export const clearPrivilegedEmployeeImport = createAsyncThunk(
  "employees/clearPrivilegedEmployeeImport",
  async (id, { rejectWithValue }) => {
    try {
      const { data: user, error: fetchError } = await supabase
        .from("company_user")
        .select("id, role")
        .eq("id", id)
        .eq("status", statuses.ACTIVE)
        .single();

      if (fetchError) throw fetchError;
      if (!user || !COMPANY_PRIVILEGED_ROLES.includes(user.role)) {
        throw new Error("User is not a privileged company account");
      }

      const { error: trainingArchiveError } = await supabase
        .from("training_survey_form")
        .update({ status: statuses.ARCHIVED })
        .eq("emp_id", id);

      if (trainingArchiveError) throw trainingArchiveError;

      const { error: clearError } = await supabase
        .from("company_user")
        .update({
          data: null,
          emp_id: null,
          department_id: null,
          employment_type: null,
          batch_job_id: null,
          manager_id: null,
          stipend_amount: null,
        })
        .eq("id", id);

      if (clearError) throw clearError;

      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const deleteByCompanyId = createAsyncThunk(
  "employees/deleteByCompanyId",
  async (id, { rejectWithValue }) => {
    try {
      const privilegedRoleFilter = `(${COMPANY_PRIVILEGED_ROLES.map((r) => `"${r}"`).join(",")})`;

      // Archive regular employees and remove their auth accounts
      const { data: usersToDelete, error: fetchError } = await supabase
        .from("company_user")
        .select("user_id")
        .eq("company_id", id)
        .not("role", "in", privilegedRoleFilter)
        .eq("status", statuses.ACTIVE);

      if (fetchError) throw fetchError;

      const { error: updateError } = await supabase
        .from("company_user")
        .update({ status: statuses.ARCHIVED })
        .eq("company_id", id)
        .not("role", "in", privilegedRoleFilter)
        .eq("status", statuses.ACTIVE);

      if (updateError) throw updateError;

      const deletePromises = (usersToDelete || [])
        .filter((user) => user.user_id)
        .map((user) => deleteAuthUserSafely(user.user_id));

      if (deletePromises.length > 0) {
        await Promise.all(deletePromises);
      }

      // Clear imported employee fields for company_admin / SDF (keep login + role)
      const { data: privilegedUsers, error: privilegedFetchError } =
        await supabase
          .from("company_user")
          .select("id")
          .eq("company_id", id)
          .in("role", COMPANY_PRIVILEGED_ROLES)
          .eq("status", statuses.ACTIVE);

      if (privilegedFetchError) throw privilegedFetchError;

      if (privilegedUsers?.length) {
        const privilegedIds = privilegedUsers.map((user) => user.id);

        const { error: trainingArchiveError } = await supabase
          .from("training_survey_form")
          .update({ status: statuses.ARCHIVED })
          .in("emp_id", privilegedIds);

        if (trainingArchiveError) throw trainingArchiveError;

        const { error: clearPrivilegedError } = await supabase
          .from("company_user")
          .update({
            data: null,
            emp_id: null,
            department_id: null,
            employment_type: null,
            batch_job_id: null,
            manager_id: null,
            stipend_amount: null,
          })
          .eq("company_id", id)
          .in("role", COMPANY_PRIVILEGED_ROLES)
          .eq("status", statuses.ACTIVE);

        if (clearPrivilegedError) throw clearPrivilegedError;
      }

      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const addEmployee = createAsyncThunk(
  "employees/addEmployee",
  async (data, { rejectWithValue }) => {
    try {
      let employeeData = {};

      const empIdTrimmed = data.emp_id != null ? String(data.emp_id).trim() : "";
      const hasEmpId = empIdTrimmed !== "";

      if (hasEmpId) {
        const { data: existingUserByEmpId, error: existingEmpIdError } = await supabase
          .from("company_user")
          .select("*")
          .eq("emp_id", data.emp_id)
          .eq("company_id", data.company_id)
          .order("created_at", { ascending: false });

        if (existingEmpIdError && existingEmpIdError.code != "PGRST116")
          throw existingEmpIdError;

        if (existingUserByEmpId && existingUserByEmpId.length > 0) {
          const hasActiveEmpId = existingUserByEmpId.some(
            (user) => user.status === statuses.ACTIVE
          );
          if (hasActiveEmpId) {
            return rejectWithValue("Personnel Number already exists. Please use a different Personnel Number.");
          }
        }
      }

      const payload = { ...data, emp_id: hasEmpId ? data.emp_id : null };

      let userId = null;
      if (payload.email) {
        // check if user already exists using email
        const { data: existingUserByEmail, error: existingUserError } = await supabase
          .from("company_user")
          .select("*")
          .eq("email", payload.email)
          .order("created_at", { ascending: false });

        if (existingUserError && existingUserError.code != "PGRST116")
          throw existingUserError;

        if (existingUserByEmail && existingUserByEmail.length > 0) {
          const hasActive = existingUserByEmail.some(
            (user) => user.status === statuses.ACTIVE
          );
          const allArchived = existingUserByEmail.every(
            (user) => user.status === statuses.ARCHIVED
          );

          if (hasActive) {
            return rejectWithValue(messages.USER_ALREADY_EXISTS);
          } else if (allArchived) {
            // Update the first archived user only
            const firstArchivedUser = existingUserByEmail[0];
            // const res = await sendInvite(null, data.email, roleType.EMPLOYEE);
            // if (!res.success) {
            //   return rejectWithValue(res.error.message);
            // }
            // userId = res.userId;

            const { data: authData, error: authError } = await supabase
              .from("company_user")
              .update({ ...payload, user_id: userId })
              .eq("id", firstArchivedUser.id)
              .select("id,company_id,data,emp_id,user_id")
              .single();
            if (authError) throw authError;
            employeeData = authData;
          }
        } else {
          // No existing user found with this email, send new invite
          // const res = await sendInvite(null, data.email, roleType.EMPLOYEE);
          // if (!res.success) {
          //   return rejectWithValue(res.error.message);
          // }
          // userId = res.userId;
        }
      }

      // Only insert new record if we haven't updated an archived one
      if (!employeeData.id) {
        const { data: employee, error } = await supabase
          .from("company_user")
          .insert({ 
            ...payload, 
            user_id: userId, 
            role: roleType.EMPLOYEE,
            status: statuses.ACTIVE 
          })
          .select("id,company_id,data,emp_id,user_id")
          .single();

        if (error) throw error;
        employeeData = employee;
      }

      let formattedData = {
        id: employeeData.id,
        company_id: employeeData.company_id || "Unknown",
        data: employeeData.data,
        emp_id: employeeData.emp_id,
        invited: employeeData.user_id ? true : false,
      };

      return formattedData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const sendInviteToEmployee = createAsyncThunk(
  "employees/sendInviteToEmployee",
  async (data, { rejectWithValue }) => {
    try {
      const companyId = getLocalData("sdfUser")?.company_id;
      const userId = getLocalData("sdfUser")?.user_id;
      
      if (!companyId || !userId) {
        return rejectWithValue("Company ID or User ID not found");
      }

      // Insert batch job record for send invite
      const { data: batchJobData, error: batchError } = await supabase
        .from("batch_jobs")
        .insert({
          company_id: companyId,
          created_by: userId,
          input_data: {
            selectedRows: data,
            company_id: companyId
          },
          status: statuses.PROCESSING,
          total_records: data.length,
          processed_records: 0,
          job_type: jobType.SEND_INVITE,
        })
        .select("*")
        .single();

      if (batchError) {
        return rejectWithValue(batchError.message);
      }

      return batchJobData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Redux Slice
const employeeSlice = createSlice({
  name: "employees",
  initialState: {
    employees: [],
    loading: false,
    error: null,
    batch_job_id: null,
  },
  reducers: {
    addBatchJobId: (state, action) => {
      state.batch_job_id = action.payload;
    },
    addEmployees: (state, action) => {
      state.employees = [...state.employees, action.payload];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmployees.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchEmployees.fulfilled, (state, action) => {
        state.loading = false;
        state.employees = action.payload.map((ele) => {
          return { ...ele.seta_json, id: ele.id };
        });
      })
      .addCase(fetchEmployees.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(uploadEmployees.fulfilled, (state, action) => {
        // state.employees.push(...action.payload);
        // toast.success(messages.EMPLOYESS_UPLOADED_SUCCESSFULLY);
      })
      .addCase(deleteByCompanyId.fulfilled, (state, action) => {
        state.employees = [];
      })
      .addCase(updateEmployee.fulfilled, (state, action) => {
        state.employees = state.employees.map((emp) =>
          emp.id === action.payload.id ? { ...emp, data: action.payload } : emp
        );
      })
      .addCase(deleteEmployee.fulfilled, (state, action) => {
        state.employees = state.employees.filter(
          (emp) => emp.id !== action.payload
        );
      })
      .addCase(addEmployee.pending, (state) => {
        state.loading = true;
      })
      .addCase(addEmployee.fulfilled, (state, action) => {
        state.loading = false;
        state.employees = [...state.employees, action.payload];
      })
      .addCase(addEmployee.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { addBatchJobId, addEmployees } = employeeSlice.actions;

export default employeeSlice.reducer;
